import { z } from 'zod';

import { isoDatetimeToDate } from '../codecs';
import { TenantAttribute } from './tenant-attribute';

export type TenantId = Tenant['id'];

export type Tenant = z.output<typeof tenantSchema>;
export type TenantDTO = z.input<typeof tenantSchema>;

export const tenantSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  attributes: z.array(z.enum(TenantAttribute)),
});

// ***** V2 ***** //

export enum ProductId {
  CompeteVision = 'competeVision',
  IMU = 'imu',
}

export enum SubscriptionStatus {
  Active = 'active',
  Paused = 'paused',
  Cancelled = 'cancelled',
  Inactive = 'inactive',
}

export type TenantSubscription = z.output<typeof tenantSubscriptionSchema>;
export type TenantSubscriptionDTO = z.input<typeof tenantSubscriptionSchema>;

export const tenantSubscriptionSchema = z.object({
  /** The product id of the subscription */
  productId: z.enum(ProductId),
  /** The subscription start date */
  startedAt: isoDatetimeToDate,
  /** The subscription end date */
  endedAt: isoDatetimeToDate.optional(),
  /** The subscriptions current status */
  status: z.enum(SubscriptionStatus),
  /**
   * The feature flags that are enabled for the subscription
   * e.g. ['beta-customer', 'explore-shot-stats']
   */
  featureFlags: z.array(z.string()),
  /**
   * The feature data for the subscription
   * e.g. { 'device-service-projects': ['1', '2', '3'] }
   */
  featureData: z.record(z.string(), z.unknown()),
});

export type TenantV2 = z.output<typeof tenantV2Schema>;
export type TenantV2DTO = z.input<typeof tenantV2Schema>;

export const tenantV2Schema = z.object({
  /** The unique identifier of the tenant, also used in cognito groups */
  id: z.string(),
  /** The name of the tenant */
  name: z.string(),
  /** The time of creation of the tenant */
  createdAt: isoDatetimeToDate,
  /** The time of the last update of the tenant */
  updatedAt: isoDatetimeToDate,
  /** Whether the tenant is deactivated */
  deactivated: z.boolean(),
  /** The product subscriptions by this tenant */
  subscriptions: z.array(tenantSubscriptionSchema),
});
